import usePresence from '../../hooks/usePresence';
import {
  professionalStatusMeta,
  shouldShowProfessionalStatus,
  contactAvailabilityMeta,
  shouldShowContactAvailability,
} from '../../lib/professionalStatus';

/**
 * ConversationHeaderPresence — renders the small "Active now / Active
 * 5 minutes ago" subtitle on a 1:1 chat header. For group chats this
 * component intentionally returns null — group presence would need
 * per-participant fan-out and isn't part of this iteration.
 *
 * When the peer has set a manual Professional Status (e.g. "On Set")
 * AND/OR a Contact Availability (e.g. "Limited Availability"), we
 * append compact pills. The subtitle can read e.g.:
 *     "Active now · On Set"
 *     "Active 2 minutes ago · On Set · Limited"
 *
 * Status visibility piggy-backs on the same `show_presence` privacy
 * rule the backend applies — if presence is hidden, status and
 * availability are hidden too (see usePresence.js +
 * /api/users/{id}/presence).
 *
 * `peerId` is the OTHER user's id (i.e. not the current user). When
 * the peer has hidden their presence (privacy), the API returns
 * `visible: false` and this component renders nothing — no fake
 * "online" status, no last-seen leak.
 */
export default function ConversationHeaderPresence({ peerId, fallback = null }) {
  const { online, label, visible, professionalStatus, contactAvailability } = usePresence(peerId);
  if (!visible || !label) return fallback;
  const showStatus = shouldShowProfessionalStatus(professionalStatus);
  const statusMeta = showStatus ? professionalStatusMeta(professionalStatus) : null;
  const showAvail = shouldShowContactAvailability(contactAvailability);
  const availMeta = showAvail ? contactAvailabilityMeta(contactAvailability) : null;
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid="conversation-presence">
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full ${online ? 'bg-emerald-500' : 'bg-theme-muted/40'}`}
        aria-hidden="true"
      />
      <span data-testid={online ? 'conversation-presence-online' : 'conversation-presence-offline'}>{label}</span>
      {statusMeta && (
        <>
          <span aria-hidden="true" className="text-theme-muted/60">·</span>
          <span
            className="inline-flex items-center gap-1"
            data-testid="conversation-professional-status"
            title={`Professional status: ${statusMeta.label}`}
          >
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${statusMeta.dot}`} aria-hidden="true" />
            <span>{statusMeta.label}</span>
          </span>
        </>
      )}
      {availMeta && (
        <>
          <span aria-hidden="true" className="text-theme-muted/60">·</span>
          <span
            className="inline-flex items-center gap-1"
            data-testid="conversation-contact-availability"
            title={`Contact availability: ${availMeta.label}`}
          >
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${availMeta.dot}`} aria-hidden="true" />
            <span>{availMeta.label.replace(' Availability', '')}</span>
          </span>
        </>
      )}
    </span>
  );
}
